// @givmo/compliance — the review's reads, decoded (CCM-R1-REVIEW-WEB).
//
// The proposal family's wire, both doors alike: the open proposal and a revision
// (`ComplianceProposalRevisionOut`), a revision's items (`ComplianceProposalItemOut`, each rule's
// item with its result and the organization's latest decision on its key), its results
// (`ComplianceAssessmentResultOut`) and a revision's review (`ComplianceProposalReviewOut`).
//
// Tolerant, as every decode in this module: an unknown field is ignored, a missing one is absent,
// and nothing throws. Every field these screens read beyond the committed snapshot is read here off
// `unknown` and absent from an older backend: the items' `decision`, the revision's `review_id`, a
// rule's presentation (G1), a question's source words and citation (G2), a condition's catalog parts
// (G11) and a met result's required evidence (J1). A list read arrives as the bare rows (the client's
// list interceptor unwraps `{data, meta, links}`) or, from a transport that does not, as the
// envelope: the module's pager (`compliance-queries.ts`) takes both before a row reaches here (G10).

import { firstErrorCode, isGivmoError } from '@givmo/api-client';
import {
  decodeProvenanceOf,
  filingFamilyOf,
  type FilingFamily,
  type RuleProvenance,
} from './compliance-decode';
import {
  decodeRuleAnchor,
  decodeWorkedOutDate,
  type RuleAnchor,
  type WorkedOutDate,
} from './compliance-filings-decode';
import { decodeFeeChart, type FormFeeChart } from './compliance-preparation-decode';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function str(value: unknown): string {
  return typeof value === 'string' ? value : '';
}
function optionalStr(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}
function int(value: unknown, fallback = 0): number {
  return typeof value === 'number' && Number.isInteger(value) ? value : fallback;
}
function optionalInt(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isInteger(value) ? value : undefined;
}
function stringList(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
}
function records(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value) ? value.filter(isRecord) : [];
}
function counts(value: unknown): Record<string, number> {
  const out: Record<string, number> = {};
  if (!isRecord(value)) return out;
  for (const [key, n] of Object.entries(value)) {
    if (typeof n === 'number' && Number.isFinite(n)) out[key] = n;
  }
  return out;
}
function dimensionsOf(value: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  if (!isRecord(value)) return out;
  for (const [key, v] of Object.entries(value)) if (typeof v === 'string') out[key] = v;
  return out;
}

// ── Rules and conditions, by address ───────────────────────────────────────────────────────────

/** A rule, by jurisdiction and rule id. */
export interface RuleRef {
  jurisdictionCode: string;
  ruleId: string;
}
function ruleRefOf(raw: unknown): RuleRef | undefined {
  if (!isRecord(raw)) return undefined;
  const jurisdictionCode = str(raw.jurisdiction_code);
  const ruleId = str(raw.rule_id);
  return jurisdictionCode.length > 0 && ruleId.length > 0
    ? { jurisdictionCode, ruleId }
    : undefined;
}
/** One rule's key, as the review's maps hold it. */
export function ruleKey(ref: RuleRef): string {
  return `${ref.jurisdictionCode}:${ref.ruleId}`;
}

/** Where a condition is written: a rule's own payload or a reviewed reading's addition. */
export interface ConditionAddress extends RuleRef {
  origin: string;
  path: string;
}
function addressOf(raw: unknown): ConditionAddress | undefined {
  const ref = ruleRefOf(raw);
  if (ref === undefined || !isRecord(raw)) return undefined;
  return { ...ref, origin: str(raw.origin), path: str(raw.path) };
}
/** One condition's key, as the review's maps hold it. */
export function addressKey(address: ConditionAddress): string {
  return `${ruleKey(address)}:${address.origin}:${address.path}`;
}

// ── The revision ───────────────────────────────────────────────────────────────────────────────

export interface ReviewRevision {
  revisionId: string;
  proposalId: string;
  sequence: number;
  isLive: boolean;
  stale: boolean;
  staleReasons: string[];
  asOf: string;
  /** The digest the review echoes: the revision exactly as it was shown. */
  payloadSha256: string;
  resultCounts: Record<string, number>;
  itemCounts: Record<string, number>;
  /** The review recorded on this revision, once there is one: a revision takes one review. */
  reviewId?: string;
}

export function decodeRevision(raw: unknown): ReviewRevision | undefined {
  if (!isRecord(raw)) return undefined;
  const revisionId = str(raw.revision_id);
  if (revisionId.length === 0) return undefined;
  return {
    revisionId,
    proposalId: str(raw.proposal_id),
    sequence: int(raw.sequence, 1),
    isLive: raw.is_live === true,
    stale: raw.stale === true,
    staleReasons: stringList(raw.stale_reasons),
    asOf: str(raw.as_of),
    payloadSha256: str(raw.payload_sha256),
    resultCounts: counts(raw.result_counts),
    itemCounts: counts(raw.item_counts),
    reviewId: optionalStr(raw.review_id),
  };
}

/** `GET …/proposal`: the open proposal's newest revision, where there is one. */
export function decodeProposal(raw: unknown): ReviewRevision | undefined {
  return isRecord(raw) ? decodeRevision(raw.live_revision) : undefined;
}

// ── A result ───────────────────────────────────────────────────────────────────────────────────

export interface ResultCondition {
  path: ConditionAddress;
  kind: string;
  outcome: string;
  unknownReason?: string;
  /** The period a measured condition read, as `fiscal:2025-01-01/2025-12-31`. */
  measuredPeriod?: string;
  inputRefs: string[];
  /** The condition's words as the record composes them. */
  words: string;
  sourceWords?: string;
  reasonWords?: string;
  /**
   * Cut 1008-122: the question in Givmo's own words, where a reviewed reading asks it rather than a
   * source (Maryland's "filed before?", W5). Its condition's `words` are then Givmo's name for it
   * (W5-r), never a source's.
   */
  moduleWords?: string;
  /** Cut 1008-122 (J3): a second source's words beside `sourceWords`, where two sources disagree. */
  secondSourceWords?: string;
  /** Each quote's citation, as the record names its source (the source's display label). */
  sourceDisplayCitation?: string;
  secondSourceDisplayCitation?: string;
  /** G11: a catalog prong's own parts, read off the pinned rule version. */
  comparator?: string;
  measure?: string;
  amountUsd?: string;
  count?: number;
  sourceScope?: string;
  excludes: string[];
  personnelKind?: string;
}

export interface FactReading {
  requirement: string;
  factKey: string;
  dimensions: Record<string, string>;
  state: string;
  value: unknown;
  /**
   * CCM-R1-FACTS-W5-NAMED: the record's name for a fact whose question Givmo asks in its own words
   * (W5-r), whatever the flag in force reads; absent on every other fact.
   */
  moduleName?: string;
  /**
   * The day its source dates it (`provenance.source_date`): for a figure counted so far, the day it is
   * counted through (cut 1008-122's C1).
   */
  sourceDate?: string;
  recordedByUserId?: string;
  recordedOn?: string;
  /** Where the value came from (`irs_record` for Givmo's IRS records) and how it was read. */
  sourceKind?: string;
  method?: string;
}
function readingOf(row: Record<string, unknown>): FactReading {
  const provenance = isRecord(row.provenance) ? row.provenance : {};
  return {
    requirement: str(row.requirement),
    factKey: str(row.fact_key),
    dimensions: dimensionsOf(row.dimensions),
    state: str(row.state),
    value: row.value,
    moduleName: optionalStr(row.module_name),
    sourceDate: optionalStr(provenance.source_date),
    recordedByUserId: optionalStr(provenance.recorded_by_user_id),
    recordedOn: optionalStr(provenance.recorded_on),
    sourceKind: optionalStr(provenance.source_kind),
    method: optionalStr(provenance.method),
  };
}

/** The rule a result is for, with its presentation (G1) and its source. */
export interface ResultRule extends RuleRef {
  jurisdictionName: string;
  ruleVersionId: string;
  ruleClass: string;
  provenance: RuleProvenance;
  /** How the calendar names the rule; absent for a rule that is no filing. */
  displayTitle?: string;
  family?: FilingFamily;
  numberLabel?: string;
  anchor?: RuleAnchor;
}

/** J1: the evidence a met result's source may ask for, in its own sentence, with its citation. */
export interface RequiredEvidence {
  quotedSourceText: string;
  displayCitation?: string;
}

/**
 * Cut 1008-122 (MD-25K's G2): what a result's line leaves open, Givmo's words (W4) then the source's,
 * cited, shown beside the result it qualifies.
 */
export interface OpenDetail {
  words: string;
  quotedSourceText: string;
  displayCitation?: string;
}

export interface ReviewResult {
  resultId: string;
  result: string;
  rule: ResultRule;
  conditions: ResultCondition[];
  inputsUsed: FactReading[];
  exclusion?: { kind: string; liftingRule?: RuleRef; lifts: RuleRef[]; paths: ConditionAddress[] };
  issue?: { kind: string; sourceWords: string[]; reasonWords: string[] };
  requiredEvidence?: RequiredEvidence;
  openDetail?: OpenDetail;
}

function conditionOf(row: Record<string, unknown>): ResultCondition | undefined {
  const path = addressOf(row.path);
  if (path === undefined) return undefined;
  return {
    path,
    kind: str(row.kind),
    outcome: str(row.outcome),
    unknownReason: optionalStr(row.unknown_reason),
    measuredPeriod: optionalStr(row.measured_period),
    inputRefs: stringList(row.input_refs),
    words: str(row.words),
    sourceWords: optionalStr(row.source_words),
    reasonWords: optionalStr(row.reason_words),
    moduleWords: optionalStr(row.module_words),
    secondSourceWords: optionalStr(row.second_source_words),
    sourceDisplayCitation: optionalStr(row.source_display_citation),
    secondSourceDisplayCitation: optionalStr(row.second_source_display_citation),
    comparator: optionalStr(row.comparator),
    measure: optionalStr(row.measure),
    // Money is exact decimal text, end to end.
    amountUsd: optionalStr(row.amount_usd),
    count: optionalInt(row.count),
    sourceScope: optionalStr(row.source_scope),
    excludes: stringList(row.excludes),
    personnelKind: optionalStr(row.personnel_kind),
  };
}

function requiredEvidenceOf(raw: unknown): RequiredEvidence | undefined {
  if (!isRecord(raw)) return undefined;
  const quotedSourceText = optionalStr(raw.quoted_source_text);
  if (quotedSourceText === undefined) return undefined;
  return { quotedSourceText, displayCitation: optionalStr(raw.display_citation) };
}

function openDetailOf(raw: unknown): OpenDetail | undefined {
  if (!isRecord(raw)) return undefined;
  const words = optionalStr(raw.words);
  const quotedSourceText = optionalStr(raw.quoted_source_text);
  if (words === undefined || quotedSourceText === undefined) return undefined;
  return { words, quotedSourceText, displayCitation: optionalStr(raw.display_citation) };
}

export function decodeResult(raw: unknown): ReviewResult | undefined {
  if (!isRecord(raw)) return undefined;
  const rule = isRecord(raw.rule) ? raw.rule : {};
  const ref = ruleRefOf(rule);
  if (ref === undefined) return undefined;
  const exclusion = isRecord(raw.exclusion) ? raw.exclusion : undefined;
  const issue = isRecord(raw.issue) ? raw.issue : undefined;
  return {
    resultId: str(raw.result_id),
    result: str(raw.result),
    rule: {
      ...ref,
      jurisdictionName: str(rule.jurisdiction_name),
      ruleVersionId: str(rule.rule_version_id),
      ruleClass: str(rule.rule_class),
      provenance: decodeProvenanceOf(rule.provenance),
      displayTitle: optionalStr(rule.display_title),
      family: filingFamilyOf(rule.filing_family),
      numberLabel: optionalStr(rule.number_label),
      anchor: decodeRuleAnchor(rule.anchor),
    },
    conditions: records(raw.conditions).flatMap((row) => {
      const condition = conditionOf(row);
      return condition !== undefined ? [condition] : [];
    }),
    inputsUsed: records(raw.inputs_used).map(readingOf),
    exclusion:
      exclusion !== undefined
        ? {
            kind: str(exclusion.kind),
            paths: records(exclusion.paths).flatMap((entry) => {
              const address = addressOf(entry);
              return address !== undefined ? [address] : [];
            }),
            liftingRule: ruleRefOf(exclusion.lifting_rule),
            lifts: records(exclusion.lifts).flatMap((row) => {
              const lifted = ruleRefOf(row);
              return lifted !== undefined ? [lifted] : [];
            }),
          }
        : undefined,
    issue:
      issue !== undefined
        ? {
            kind: str(issue.kind),
            sourceWords: stringList(issue.source_words),
            reasonWords: stringList(issue.reason_words),
          }
        : undefined,
    requiredEvidence: requiredEvidenceOf(raw.required_evidence),
    openDetail: openDetailOf(raw.open_detail),
  };
}

// ── An item, with the organization's latest decision on its key ────────────────────────────────

export interface ItemDecision {
  decisionId: string;
  revisionId: string;
  decision: string;
  reason?: string;
  effect: string;
  obligationId?: string;
  decidedByUserId: string;
  decidedAt?: string;
  /** `web`, `connector` (the organization's own AI) or `desk`. */
  channel: string;
  /** Whether this item still rests on the evidence the decision was made on (JQ1's reading). */
  evidenceUnchanged: boolean;
}

export interface Decides extends RuleRef {
  resultId: string;
  paths: ConditionAddress[];
}
function decidesOf(raw: unknown): Decides[] {
  return records(raw).flatMap((row) => {
    const ref = ruleRefOf(row);
    if (ref === undefined) return [];
    return [
      {
        ...ref,
        resultId: str(row.result_id),
        paths: records(row.paths).flatMap((entry) => {
          const address = addressOf(entry);
          return address !== undefined ? [address] : [];
        }),
      },
    ];
  });
}

export interface ReviewQuestion {
  factKey: string;
  dimensions: Record<string, string>;
  valueType: string;
  state: string;
  decides: Decides[];
  /** The profile's own field that answers it, where one does; absent, no door answers it yet. */
  answerParameterKey?: string;
  /** G2: an organization answer's question in its source's own words, and their citation. */
  sourceWords?: string;
  sourceDisplayCitation?: string;
  /** Cut 1008-122: the question in Givmo's own words, where no source asks it (W5): no citation. */
  moduleWords?: string;
  /** A source's own example beside the question, and its citation (cut 1008-92, D-CCM-75 J-C). */
  example?: { quotedSourceText: string; displayCitation?: string };
  /** The fee to file what the answer decides, as its form prints it (cut 1008-92, J-C). */
  fee?: { formNumber: string; chart: FormFeeChart };
}

function questionExampleOf(raw: unknown): ReviewQuestion['example'] {
  if (!isRecord(raw)) return undefined;
  const quotedSourceText = optionalStr(raw.quoted_source_text);
  if (quotedSourceText === undefined) return undefined;
  const displayCitation = optionalStr(raw.display_citation);
  return { quotedSourceText, ...(displayCitation !== undefined ? { displayCitation } : {}) };
}

function questionFeeOf(raw: unknown): ReviewQuestion['fee'] {
  if (!isRecord(raw)) return undefined;
  const formNumber = optionalStr(raw.form_number);
  const chart = decodeFeeChart(raw.chart);
  return formNumber !== undefined && chart !== undefined ? { formNumber, chart } : undefined;
}

export interface ReviewItem {
  itemKey: string;
  kind: string;
  position: number;
  sortOn?: string;
  resultId?: string;
  jurisdictionCode?: string;
  ruleId?: string;
  requirement?: string;
  existingObligationId?: string;
  previewed: WorkedOutDate[];
  exclusionKind?: string;
  question?: ReviewQuestion;
  issueKind?: string;
  issuePath?: ConditionAddress;
  issueBlocks: Decides[];
  change?: {
    obligationId: string;
    changes: string[];
    result: string;
    /** The day Givmo's rule took the version the review assessed (N36's day; be#2770). */
    assessedRuleVersionLoadedOn?: string;
  };
  result?: ReviewResult;
  decision?: ItemDecision;
  /** CCM-R1-REVIEW-ASSIGN: the open ask on this item, served to every reader of the review. */
  ask?: ItemAsk;
}

/**
 * An open ask on an item (`ComplianceProposalItemAskOut`, be#2776). Its day is in every line that
 * names it (N43), so an ask served without one is read as none.
 */
export interface ItemAsk {
  askId: string;
  askedUserId: string;
  askedByUserId: string;
  askedAt: string;
  note?: string;
  /**
   * How the ask was made: `web`, by a person in Givmo, or `connector`, through their connected AI.
   * An item's own ask serves it (CCM-R1-REVIEW-ASK-CHANNEL-ON-ITEM, be#3194), as the asks list
   * does, and a card reads it there.
   */
  channel?: string;
}

function itemAskOf(raw: unknown): ItemAsk | undefined {
  if (!isRecord(raw)) return undefined;
  const askId = optionalStr(raw.ask_id);
  const askedUserId = optionalStr(raw.asked_user_id);
  const askedByUserId = optionalStr(raw.asked_by_user_id);
  const askedAt = optionalStr(raw.asked_at);
  if (
    askId === undefined ||
    askedUserId === undefined ||
    askedByUserId === undefined ||
    askedAt === undefined
  )
    return undefined;
  const note = optionalStr(raw.note)?.trim();
  const channel = optionalStr(raw.channel);
  return {
    askId,
    askedUserId,
    askedByUserId,
    askedAt,
    ...(note !== undefined && note.length > 0 ? { note } : {}),
    ...(channel !== undefined ? { channel } : {}),
  };
}

/** An ask as the asks list serves it (`ComplianceProposalAskOut`): its item, and where it stands. */
export interface ReviewAsk extends ItemAsk {
  revisionId: string;
  itemKey: string;
  /** `open`, `resolved` (computed off the live revision), `withdrawn` or `replaced`. */
  state: string;
}

export function decodeAsk(raw: unknown): ReviewAsk | undefined {
  const ask = itemAskOf(raw);
  if (ask === undefined || !isRecord(raw)) return undefined;
  const revisionId = optionalStr(raw.revision_id);
  const itemKey = optionalStr(raw.item_key);
  if (revisionId === undefined || itemKey === undefined) return undefined;
  return { ...ask, revisionId, itemKey, state: str(raw.state) };
}

function decisionOf(raw: unknown): ItemDecision | undefined {
  if (!isRecord(raw)) return undefined;
  const decisionId = str(raw.decision_id);
  if (decisionId.length === 0) return undefined;
  return {
    decisionId,
    revisionId: str(raw.revision_id),
    decision: str(raw.decision),
    reason: optionalStr(raw.reason),
    effect: str(raw.effect),
    obligationId: optionalStr(raw.obligation_id),
    decidedByUserId: str(raw.decided_by_user_id),
    decidedAt: optionalStr(raw.decided_at),
    channel: str(raw.channel),
    // Read as written: anything but a plain `true` keeps the item open for a decision again.
    evidenceUnchanged: raw.evidence_unchanged === true,
  };
}

/** A change item's change: the filing on the calendar, what moved, and how the rule reads now. */
function changeOf(change: Record<string, unknown>): NonNullable<ReviewItem['change']> {
  const assessedRuleVersionLoadedOn = optionalStr(change.assessed_rule_version_loaded_on);
  return {
    obligationId: str(change.obligation_id),
    changes: stringList(change.changes),
    result: str(change.result),
    ...(assessedRuleVersionLoadedOn !== undefined ? { assessedRuleVersionLoadedOn } : {}),
  };
}

export function decodeItem(raw: unknown): ReviewItem | undefined {
  if (!isRecord(raw)) return undefined;
  const itemKey = str(raw.item_key);
  if (itemKey.length === 0) return undefined;
  const question = isRecord(raw.question) ? raw.question : undefined;
  const change = isRecord(raw.change) ? raw.change : undefined;
  return {
    itemKey,
    kind: str(raw.kind),
    position: int(raw.position),
    sortOn: optionalStr(raw.sort_on),
    resultId: optionalStr(raw.result_id),
    jurisdictionCode: optionalStr(raw.jurisdiction_code),
    ruleId: optionalStr(raw.rule_id),
    requirement: optionalStr(raw.requirement),
    existingObligationId: optionalStr(raw.existing_obligation_id),
    previewed: records(raw.previewed).map(decodeWorkedOutDate),
    exclusionKind: optionalStr(raw.exclusion_kind),
    question:
      question !== undefined
        ? {
            factKey: str(question.fact_key),
            dimensions: dimensionsOf(question.dimensions),
            valueType: str(question.value_type),
            state: str(question.state),
            decides: decidesOf(question.decides),
            answerParameterKey: optionalStr(question.answer_parameter_key),
            sourceWords: optionalStr(question.source_words),
            sourceDisplayCitation: optionalStr(question.source_display_citation),
            moduleWords: optionalStr(question.module_words),
            example: questionExampleOf(question.example),
            fee: questionFeeOf(question.fee),
          }
        : undefined,
    issueKind: optionalStr(raw.issue_kind),
    issuePath: addressOf(raw.issue_path),
    issueBlocks: decidesOf(raw.issue_blocks),
    change: change !== undefined ? changeOf(change) : undefined,
    result: decodeResult(raw.result),
    decision: decisionOf(raw.decision),
    ask: itemAskOf(raw.ask),
  };
}

// ── The review recorded on a revision ──────────────────────────────────────────────────────────

export interface RecordedReview {
  reviewId: string;
  revisionId: string;
  committedByUserId: string;
  committedAt?: string;
  channel: string;
  decisions: {
    decisionId: string;
    itemKey: string;
    itemKind: string;
    decision: string;
    reason?: string;
    effect: string;
    obligationId?: string;
    decidedByUserId: string;
    decidedAt?: string;
    channel: string;
  }[];
}

export function decodeReview(raw: unknown): RecordedReview | undefined {
  if (!isRecord(raw)) return undefined;
  const reviewId = str(raw.review_id);
  if (reviewId.length === 0) return undefined;
  return {
    reviewId,
    revisionId: str(raw.revision_id),
    committedByUserId: str(raw.committed_by_user_id),
    committedAt: optionalStr(raw.committed_at),
    channel: str(raw.channel),
    decisions: records(raw.decisions).map((row) => ({
      decisionId: str(row.decision_id),
      itemKey: str(row.item_key),
      itemKind: str(row.item_kind),
      decision: str(row.decision),
      reason: optionalStr(row.reason),
      effect: str(row.effect),
      obligationId: optionalStr(row.obligation_id),
      decidedByUserId: str(row.decided_by_user_id),
      decidedAt: optionalStr(row.decided_at),
      channel: str(row.channel),
    })),
  };
}

// ── Refusals ───────────────────────────────────────────────────────────────────────────────────

/** The review's stale refusal's code (409): the revision no longer reads what was shown. */
export const REVIEW_STALE_ERROR_CODE = 'COMPLIANCE_REVIEW_STALE';

/** A 409 `COMPLIANCE_REVIEW_STALE`, with its reasons and the newest revision; else `undefined`. */
export interface StaleRefusal {
  /** The server's own words (R1, approved: D-CCM-37). */
  detail: string;
  reasons: string[];
  liveRevisionId?: string;
}

export function staleRefusalOf(error: unknown): StaleRefusal | undefined {
  if (!isGivmoError(error) || firstErrorCode(error) !== REVIEW_STALE_ERROR_CODE) return undefined;
  const entry = error.entries.find((candidate) => candidate.errorCode === REVIEW_STALE_ERROR_CODE);
  const context = isRecord(entry?.context) ? entry.context : {};
  return {
    detail: error.message,
    reasons: stringList(context.stale_reasons),
    liveRevisionId: optionalStr(context.live_revision_id),
  };
}

/** The ask's refusal of someone who can't make changes in Compliance (422); its line is N42's. */
export const ASKED_WITHOUT_ACCESS_ERROR_CODE = 'COMPLIANCE_REVIEW_ASKED_WITHOUT_ACCESS';

/** Whether an ask was refused because the person chosen can't make changes in Compliance. */
export function isAskedWithoutAccess(error: unknown): boolean {
  return isGivmoError(error) && firstErrorCode(error) === ASKED_WITHOUT_ACCESS_ERROR_CODE;
}
